import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Heart, MessageCircle, Star } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { useSite, waLinkFor } from "@/lib/SiteContext";
import { EASE, Em } from "./primitives";

const DEFAULTS = {
  kicker: "Thoughtful Gifts. Beautiful Memories.",
  heading: "Make Every\nMoment *Extra*\nSpecial.",
  subtext:
    "Thoughtfully curated gifts for birthdays, anniversaries, weddings, celebrations and every little reason to make someone smile.",
  primary_label: "Shop Gifts",
  secondary_label: "Chat on WhatsApp",
  image:
    "https://images.unsplash.com/photo-1513201099705-a9746e1e201f?q=80&w=1200&auto=format&fit=crop",
  video: "",
  badge_title: "Made with love",
  badge_sub: "Handcrafted in India",
};

export default function Hero({ content, onNavigate }) {
  const { contact } = useSite();
  const c = { ...DEFAULTS, ...(content || {}) };
  const lines = (Array.isArray(c.heading) ? c.heading : String(c.heading).split("\n")).filter(Boolean);

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pb-20 pt-32 md:pb-28 md:pt-44"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-rose/10 blur-3xl"
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 md:px-12 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="mb-8 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-rose md:text-sm"
          >
            <span className="h-px w-10 bg-rose" />
            {c.kicker}
          </motion.p>

          <motion.h1
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: { staggerChildren: 0.16, delayChildren: 0.25 },
              },
            }}
            className="font-serif text-5xl font-medium leading-[0.95] tracking-tight text-ink sm:text-6xl md:text-8xl"
          >
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  variants={{ hidden: { y: "110%" }, visible: { y: 0 } }}
                  transition={{ duration: 1.1, ease: EASE }}
                  className="block"
                >
                  <Em text={line} />
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[#5C5C5C] md:text-lg"
          >
            {c.subtext}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-shop-button"
              onClick={() => onNavigate("#shop")}
              className="group inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-medium text-cream transition-colors duration-300 hover:bg-rose hover:text-white"
            >
              {c.primary_label}
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </button>
            <a
              data-testid="hero-enquire-button"
              href={waLinkFor(contact)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-ink px-8 py-4 text-sm font-medium text-ink transition-colors duration-300 hover:bg-ink hover:text-cream"
            >
              <MessageCircle size={16} /> {c.secondary_label}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-14 flex divide-x divide-line"
          >
            {[
              ["500+", "Gifts Curated"],
              ["4.9", "Average Rating"],
              ["Pan-India", "Delivery"],
            ].map(([big, small], i) => (
              <div key={small} className={i === 0 ? "pr-8" : "px-8"}>
                <p className="font-serif text-3xl font-medium text-ink">{big}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[#5C5C5C]">
                  {small}
                </p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
          >
            <motion.div
              style={{ y: imgY }}
              className="rounded-b-[36px] rounded-t-[999px] border border-line bg-white p-3 shadow-2xl shadow-rose/10"
            >
              <div className="overflow-hidden rounded-b-[28px] rounded-t-[999px]">
                {c.video ? (
                  <video
                    src={imgUrl(c.video)}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="aspect-[3/4] w-full object-cover"
                  />
                ) : (
                  <img
                    src={imgUrl(c.image)}
                    alt="An elegantly wrapped premium gift box with a silk ribbon"
                    className="aspect-[3/4] w-full object-cover"
                    loading="eager"
                  />
                )}
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.4, ease: EASE }}
            className="absolute -bottom-6 -left-4 md:-left-10"
          >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="flex items-center gap-3 rounded-2xl border border-line bg-white/90 px-5 py-4 shadow-xl shadow-rose/10 backdrop-blur-md"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-rose/10">
                <Heart size={18} className="fill-rose text-rose" />
              </span>
              <div>
                <p className="text-sm font-semibold text-ink">{c.badge_title}</p>
                <p className="flex items-center gap-1 text-xs text-[#5C5C5C]">
                  <Star size={11} className="fill-gold text-gold" /> {c.badge_sub}
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
